import PropTypes from "prop-types";
import React from "react";
import { Helmet } from "react-helmet";
import i18n from "../i18n";
import { DEFAULT_LANGUAGE,
    DEFAULT_OG_IMAGE,
    SITE_NAME,
    SUPPORTED_LANGUAGES,
    buildAbsoluteUrl,
    buildLocalizedPath,
    buildSeoJsonLd,
    detectLanguage,
    getAlternateLinks,
    getDefaultKeywords,
    getRouteSeo,
    getPrefetchTargets,
    normalizeSeoPath, } from "../seo/siteSeo";

const SEO = ({
    title,
    titleKey,
    description,
    descriptionKey,
    url,
    ogImage,
    canonical,
    locale,
    jsonLd,
    keywords,
    noindex = false,
    path,
}) => {
    const pathname = path || (typeof window !== "undefined" ? window.location.pathname : "/");
    const normalizedPath = normalizeSeoPath(pathname);
    const lang = detectLanguage(i18n.language, pathname);
    const routeSeo = getRouteSeo(normalizedPath, lang);
    const localeMap = {
        hu: "hu_HU",
        en: "en_US",
        de: "de_DE" 
    };
    const resolvedLocale = (locale && locale.length ? locale : localeMap[lang]) || "hu_HU";
    const siteName = i18n.t("common.appName") || SITE_NAME;
    const resolvedTitle = titleKey ? i18n.t(titleKey) : title || routeSeo.title;
    const pageTitle = resolvedTitle && resolvedTitle !== siteName ? `${resolvedTitle} - ${siteName}` : siteName;
    const defaultDesc = i18n.t("seo.defaultDesc", { defaultValue: routeSeo.description });
    const resolvedDesc = descriptionKey ? i18n.t(descriptionKey) : description;
    const pageDesc = resolvedDesc || routeSeo.description || defaultDesc;
    const resolvedKeywords = []
        .concat(keywords || routeSeo.keywords || getDefaultKeywords(lang))
        .filter(Boolean);
    const canonicalPath = canonical || url || buildAbsoluteUrl(routeSeo.localizedPath || normalizedPath, lang);
    const alternateLinks = getAlternateLinks(routeSeo);
    const robotsContent = noindex
        ? "noindex, nofollow, noarchive, nosnippet"
        : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1, indexifembedded";
    const socialImage = ogImage || DEFAULT_OG_IMAGE;
    const prefetchedRoutes = getPrefetchTargets(lang).filter((target) => target !== buildLocalizedPath(routeSeo.localizedPath || normalizedPath, lang));

    const toArray = (data) => (Array.isArray(data) ? data : (data ? [data] : []));
    const defaultLd = buildSeoJsonLd({
        routeSeo,
        language: lang,
        title: pageTitle,
        description: pageDesc,
        pageUrl: canonicalPath,
    });
    const providedLd = toArray(jsonLd).map((obj) => ({
        "@context": "https://schema.org",
        inLanguage: lang,
        ...obj,
    }));
    const allLd = [...defaultLd, ...providedLd];

    return (
        <Helmet htmlAttributes={{ lang }}>
            <meta charSet="utf-8" />
            <meta
                name="viewport"
                content="width=device-width, initial-scale=1, viewport-fit=cover"
            />
            <title>{pageTitle}</title>
            <meta name="robots" content={robotsContent} />
            <meta name="googlebot" content={robotsContent} />
            <meta name="bingbot" content={robotsContent} />
            <meta name="description" content={pageDesc} />
            {!!resolvedKeywords.length && <meta name="keywords" content={resolvedKeywords.join(", ")} />}
            <meta name="author" content="Happy Solutions Kft." />
            <meta name="publisher" content={siteName} />
            <meta name="theme-color" content="#F2B34C" />
            <meta name="format-detection" content="telephone=no,address=no,email=no" />
            <meta httpEquiv="content-language" content={lang} />
            <meta name="language" content={lang} />
            <meta name="referrer" content="strict-origin-when-cross-origin" />
            <link rel="canonical" href={canonicalPath} />
            {alternateLinks.map((alt) => (
                <link key={`${alt.hrefLang}:${alt.href}`} rel="alternate" hrefLang={alt.hrefLang} href={alt.href} />
            ))}

            <link rel="dns-prefetch" href="//www.googletagmanager.com" />
            <link rel="dns-prefetch" href="//fonts.googleapis.com" />
            <link rel="dns-prefetch" href="//fonts.gstatic.com" />
            <link rel="dns-prefetch" href="//happy-releases.fra1.cdn.digitaloceanspaces.com" />
            <link rel="dns-prefetch" href="//api.happygastro.hu" />
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link rel="preconnect" href="https://happy-releases.fra1.cdn.digitaloceanspaces.com" crossOrigin="anonymous" />

            {prefetchedRoutes.map((href) => (
                <link key={`prefetch:${href}`} rel="prefetch" href={href} as="document" />
            ))}

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content={canonicalPath} />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={pageDesc} />
            <meta property="og:site_name" content={siteName} />
            <meta property="og:locale" content={resolvedLocale} />
            {SUPPORTED_LANGUAGES.filter((item) => item !== lang).map((item) => (
                <meta key={`og-locale:${item}`} property="og:locale:alternate" content={localeMap[item]} />
            ))}
            <meta property="og:image" content={socialImage} />
            <meta property="og:image:secure_url" content={socialImage} />
            <meta property="og:image:alt" content={pageTitle} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={pageDesc} />
            <meta name="twitter:image" content={socialImage} />
            <meta name="twitter:image:alt" content={pageTitle} />
            <meta name="apple-mobile-web-app-title" content={siteName} />

            {/* JSON-LD */}
            {allLd.map((obj, idx) => (
                <script key={`ld-${idx}`} type="application/ld+json">
                    {JSON.stringify(obj)}
                </script>
            ))}
        </Helmet>
    );
};

SEO.propTypes = {
    title: PropTypes.string,
    titleKey: PropTypes.string,
    description: PropTypes.string,
    descriptionKey: PropTypes.string,
    url: PropTypes.string,
    ogImage: PropTypes.string,
    canonical: PropTypes.string,
    locale: PropTypes.string,
    jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    keywords: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    noindex: PropTypes.bool,
    path: PropTypes.string,
};

export default SEO;
